/**
 * "Profile picture" — choose, preview, save or remove.
 *
 * OPENED BY CLICKING THE AVATAR. That is the whole interaction the brief asks for:
 * the picture you are looking at is the control that changes it, which is what every
 * account screen has trained people to expect, so it needed no new button in the
 * Settings header and no new row in the navigation.
 *
 * PREVIEW BEFORE SAVE, and the preview is the real thing. The chosen file is read to
 * a data URL and drawn in the same `<Avatar>` component every other surface uses, at
 * a larger size — so what you approve is literally what those surfaces will render,
 * cropped by the same circle. Nothing reaches `authStore` until "Save picture" is
 * pressed; Cancel drops the candidate and the saved picture is untouched.
 *
 * ------------------------------------------------------------------------------
 * THE PICTURE IS NOT UPLOADED ANYWHERE, AND THIS DIALOG SAYS SO ON SCREEN.
 * ------------------------------------------------------------------------------
 * There is no backend (config.USE_MOCK_API is true; see src/api/README.md), so there
 * is nowhere to PUT an image. `authApi.updateProfile` hands the value straight back
 * and the store keeps the data URL in memory — the same arrangement uploaded cover art
 * already has, for the same reason. A reload therefore loses the picture, exactly as it
 * loses the simulated session.
 *
 * That limitation is printed under the buttons rather than left in a comment, because
 * the alternative is a user who believes their photo is stored on a server. The
 * boundary is in the right place for it to stop being a limitation: when object storage
 * exists, the upload POST goes in `saveAvatar` below, and everything else — the picker,
 * the validation, the preview, the PATCH — is unchanged.
 *
 * Reuses lib/imageUpload wholesale (formats, 8MB ceiling, data-URL read, drop
 * handling) rather than restating the rules, so the avatar and the Studio's cover art
 * accept exactly the same files and reject them with the same sentences.
 *
 * The picker follows CoverPreview's pattern: a `<label>` wrapping a visually hidden
 * `<input type="file">`, which gives a click target, a real keyboard-focusable control
 * and drag-and-drop with no custom key handling. The consequence to remember is that a
 * <button> inside that label would also open the picker, so Save/Remove/Cancel sit
 * outside it.
 */
import { useEffect, useRef, useState } from 'react';
import { ImagePlus, Loader2, Trash2, Upload } from 'lucide-react';
import { Button, Modal } from '../ui';
import Avatar from '../account/Avatar';
import { USE_MOCK_API } from '../../config';
import {
  ACCEPT_ATTRIBUTE,
  ACCEPTED_IMAGE_LABEL,
  MAX_IMAGE_LABEL,
  firstImageFrom,
  readImageAsDataUrl,
  validateImageFile,
} from '../../lib/imageUpload';
import { c } from '../../styles/theme';

/**
 * How large the picture is shown while deciding on it.
 *
 * 96px against the 40px the header draws: big enough to judge a crop, small enough
 * that the dialog stays a dialog. Same circle, same component, same `object-fit`.
 */
const PREVIEW_SIZE = 96;

export interface ProfilePictureModalProps {
  open: boolean;
  onClose: () => void;
  /** The picture currently saved on the account, or null. */
  currentUrl: string | null;
  /**
   * Commits a chosen picture, or removes it with null.
   *
   * Async and allowed to reject: the store call goes through authApi, and a rejection
   * has to leave the dialog open with the candidate intact rather than closing over a
   * save that did not happen.
   */
  onSave: (avatarUrl: string | null) => Promise<void>;
}

export default function ProfilePictureModal({
  open,
  onClose,
  currentUrl,
  onSave,
}: ProfilePictureModalProps) {
  /** The chosen picture awaiting a decision. null means nothing chosen yet. */
  const [candidate, setCandidate] = useState<string | null>(null);
  /** True while the file is being read, or while the save is in flight. */
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  // Guards a setState after the dialog closed mid-read: FileReader is async and a
  // large file plus a quick Escape is enough to land the resolve on an unmounted tree.
  const liveRef = useRef(true);
  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);

  // Reopening starts clean. Without this, cancelling a choice and reopening would
  // show the rejected candidate again as though it had been saved.
  useEffect(() => {
    if (open) {
      setCandidate(null);
      setError(null);
      setDragging(false);
      setBusy(false);
    }
  }, [open]);

  async function take(files: FileList | null) {
    const file = firstImageFrom(files);
    if (!file) {
      setError('No image found there. Pick a single image file.');
      return;
    }
    const rejection = validateImageFile(file);
    if (rejection) {
      setError(rejection.message);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const dataUrl = await readImageAsDataUrl(file);
      if (!liveRef.current) return;
      setCandidate(dataUrl);
    } catch (err) {
      if (!liveRef.current) return;
      setError(err instanceof Error ? err.message : 'Could not read that image.');
    } finally {
      if (liveRef.current) setBusy(false);
    }
  }

  async function commit(value: string | null) {
    setBusy(true);
    setError(null);
    try {
      await onSave(value);
      if (liveRef.current) setBusy(false);
      onClose();
    } catch (err) {
      if (!liveRef.current) return;
      setBusy(false);
      setError(err instanceof Error ? err.message : 'Could not update your profile picture.');
    }
  }

  const hasCandidate = candidate !== null;

  return (
    <Modal open={open} onClose={onClose} title="Profile picture" width={380}>
      <p style={{ fontSize: 12, color: c.text2, margin: '0 0 16px', lineHeight: 1.5 }}>
        {hasCandidate
          ? 'This is how your new picture will look. Save it to use it across your account.'
          : 'Choose an image to use across your account.'}
      </p>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
          marginBottom: 14,
        }}
      >
        {/* The preview, and the picker, and the drop zone — one control.
            `previewUrl` is only passed once something is chosen, so before that this
            renders exactly what the account shows today. */}
        <label
          className="lyx-avatar-picker"
          style={{
            borderColor: dragging ? c.accentFill : c.border,
            background: dragging ? c.accentSoft : 'transparent',
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            void take(e.dataTransfer.files);
          }}
        >
          <input
            className="lyx-visually-hidden"
            type="file"
            accept={ACCEPT_ATTRIBUTE}
            aria-label={currentUrl || hasCandidate ? 'Change profile picture' : 'Upload a picture'}
            disabled={busy}
            onChange={(e) => {
              void take(e.target.files);
              // Clears the input so choosing the same file twice fires onChange again —
              // otherwise re-picking after a failed read does nothing.
              e.target.value = '';
            }}
          />

          <Avatar
            size={PREVIEW_SIZE}
            decorative={false}
            {...(hasCandidate ? { previewUrl: candidate } : null)}
          />

          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 11,
              fontWeight: 500,
              color: c.text2,
            }}
          >
            {busy ? (
              <Loader2 size={12} className="lyx-spin" />
            ) : hasCandidate ? (
              <ImagePlus size={12} />
            ) : (
              <Upload size={12} />
            )}
            {busy
              ? 'Reading image…'
              : hasCandidate
                ? 'Choose a different image'
                : 'Click or drop an image'}
          </span>
        </label>

        <p style={{ fontSize: 11, color: c.text2, margin: 0, textAlign: 'center' }}>
          {ACCEPTED_IMAGE_LABEL}, up to {MAX_IMAGE_LABEL}
        </p>
      </div>

      {error ? (
        <p role="alert" style={{ fontSize: 11, color: c.danger, margin: '0 0 12px' }}>
          {error}
        </p>
      ) : null}

      <Button
        full
        disabled={!hasCandidate || busy}
        onClick={() => void commit(candidate)}
        style={{ marginBottom: 8 }}
      >
        Save picture
      </Button>

      {/* Only offered when there is something to remove, so the dialog does not show a
          destructive action against the initials fallback. */}
      {currentUrl ? (
        <Button
          full
          variant="secondary"
          disabled={busy}
          onClick={() => void commit(null)}
          style={{ marginBottom: 8, color: c.danger }}
        >
          <Trash2 size={13} />
          Remove picture
        </Button>
      ) : null}

      <Button full variant="ghost" disabled={busy} onClick={onClose}>
        Cancel
      </Button>

      {/* Stated to the user, not just to the developer — see the header. */}
      <p
        style={{
          fontSize: 10,
          color: c.text2,
          lineHeight: 1.5,
          margin: '14px 0 0',
          paddingTop: 12,
          borderTop: `1px solid ${c.border}`,
        }}
      >
        {USE_MOCK_API
          ? 'Your picture is held in this browser tab only — there is no image storage connected yet, so signing out or reloading clears it.'
          : 'Your picture is saved to your account and appears anywhere you sign in.'}
      </p>
    </Modal>
  );
}
